import { computed, reactive, toRef, watch, watchEffect } from "vue";
import RowText from "@/components/input-formvuelate/RowText.vue";
import { asCur, getLabelForPDF } from "@/helpers/inputHelpers";
import {
	financieringsSchema,
	kostenSchema,
	leningdelenSchema,
	middelenSchema,
	resultaatSchema,
	situatieSchema,
	schema,
} from "../schemas/financieringsOpzetSchema";
import useLeningdelen from "./useLeningdelen";
import { sumInObject } from "@/helpers/logicHelpers";
import { useParameters } from "@/store/useParameters";
import type { LeningdeelVariable } from "@/models/LeningdeelVariable";
import { ValueType } from "@/enums/ValueType";
import { defineStore, storeToRefs } from "pinia";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { repaymentFormLabels } from "@/components/existing-mortgage/repayment-form";
import { useCalculationDate } from "@/composables/useCalculationDate";

export default defineStore("financieringsOpzet", () => {
	const leningdelenStore = useLeningdelen();
	const { mortgagePartsTotal } = storeToRefs(leningdelenStore);
	const dossierDataStore = useDossierDataStore();
	const { data } = storeToRefs(dossierDataStore);

	const { calculationDate, calculationDateString } = useCalculationDate();

	const state = reactive({
		schema: schema,
		situatieSchema: undefined as ReturnType<typeof situatieSchema> | undefined,
		kostenSchema: kostenSchema,
		middelenSchema: middelenSchema(false),
		leningdelenSchema: leningdelenSchema,
		financieringsSchema: financieringsSchema,
		resultaatSchema: resultaatSchema,
		values: toRef(() => data.value.financieringsOpzet),
	});

	const mortgagePartsToShow = computed(() => {
		if (
			data.value.financieringsOpzet.situatie === "Omzetten" ||
			data.value.financieringsOpzet.situatie === "Uitbreiding"
		) {
			return data.value.mortgage.mortgageParts.filter(
				(part) => part.existingMortgagePartId === undefined,
			);
		}

		return data.value.mortgage.mortgageParts;
	});

	const shownMortgagePartsTotal = computed(() => {
		return mortgagePartsToShow.value
			.map((part) => part.amount ?? 0)
			.reduce((a, b) => a + b, 0);
	});

	watchEffect(() => {
		state.situatieSchema = situatieSchema(calculationDateString.value);
	});

	// Update middelenSchema readonly op basis van eigenMiddelenInputType
	watchEffect(() => {
		const isAuto =
			data.value.financieringsOpzet?.eigenMiddelenInputType === "auto";
		state.middelenSchema = middelenSchema(isAuto);
	});

	// Handle OVB percentage changes
	watch(
		() => data.value.financieringsOpzet?.percentageOvb,
		(percentageOvb) => {
			if (!data.value.financieringsOpzet?.aankoopkosten) {
				return;
			}

			if (percentageOvb === "manual") {
				// Copy computed value to manual field if it exists
				if (data.value.financieringsOpzet.aankoopkosten.ovb) {
					data.value.financieringsOpzet.aankoopkosten.ovbManual =
						data.value.financieringsOpzet.aankoopkosten.ovb;
				}
				// Clear computed value
				delete data.value.financieringsOpzet.aankoopkosten.ovb;
			} else {
				// Clear manual value when switching back to percentage
				delete data.value.financieringsOpzet.aankoopkosten.ovbManual;
			}
		},
		{ immediate: true },
	);

	// Bereken ovb (only when percentage is selected)
	watchEffect(() => {
		if (!data.value.financieringsOpzet) {
			return;
		}

		const percentageOvb = data.value.financieringsOpzet.percentageOvb;

		// Only calculate when a percentage is selected (not "manual")
		if (typeof percentageOvb === "number") {
			const koopsom = asCur(
				data.value.financieringsOpzet.aankoopkosten?.koopsom,
			);

			const ovb = koopsom !== undefined ? koopsom * percentageOvb : NaN;

			if (Number.isNaN(ovb)) {
				delete data.value.financieringsOpzet.aankoopkosten?.ovb;
			} else if (data.value.financieringsOpzet.aankoopkosten) {
				data.value.financieringsOpzet.aankoopkosten.ovb = ovb;
			}
		} else {
			// Clear computed ovb when no percentage is selected or manual is selected
			delete data.value.financieringsOpzet.aankoopkosten?.ovb;
		}
	});

	// Ensure nhgInputType is set to 'auto' for existing data models
	watchEffect(() => {
		if (
			data.value.financieringsOpzet?.useNHG === "Ja" &&
			!data.value.financieringsOpzet.nhgInputType
		) {
			data.value.financieringsOpzet.nhgInputType = "auto";
		}
	});

	// Handle NHG input type changes
	watch(
		() => data.value.financieringsOpzet?.nhgInputType,
		(nhgInputType) => {
			if (!data.value.financieringsOpzet?.aankoopkosten) {
				return;
			}

			if (nhgInputType === "manual") {
				// Copy computed value to manual field if it exists
				if (data.value.financieringsOpzet.aankoopkosten.nhgHoogte) {
					data.value.financieringsOpzet.aankoopkosten.nhgHoogteManual =
						data.value.financieringsOpzet.aankoopkosten.nhgHoogte;
				}
				// Clear computed value
				delete data.value.financieringsOpzet.aankoopkosten.nhgHoogte;
			} else if (nhgInputType === "auto") {
				// Clear manual value
				delete data.value.financieringsOpzet.aankoopkosten.nhgHoogteManual;
			}
		},
		{ immediate: true },
	);

	// Bereken NHG (only when automatic)
	watchEffect(() => {
		if (!data.value.financieringsOpzet) {
			return;
		}

		const nhg = data.value.financieringsOpzet.useNHG;
		const nhgInputType = data.value.financieringsOpzet?.nhgInputType;

		if (nhg === "Ja" && nhgInputType === "auto") {
			const leninghoogte = asCur(mortgagePartsTotal.value?.hoogte);

			let alreadyPaidNHG = 0;
			for (const em of data.value.existingMortgages) {
				if (!em.nhg) {
					continue;
				}

				for (const part of em.mortgageParts ?? []) {
					const partFound = data.value.mortgage?.mortgageParts.find(
						(mp) => mp.existingMortgagePartId === part.id,
					);
					alreadyPaidNHG += partFound?.amount ?? 0;
				}
			}

			const leninghoogteToPayNHG = Math.max(
				(leninghoogte ?? 0) - alreadyPaidNHG,
				0,
			);

			const nhgBedrag = leninghoogteToPayNHG
				? useParameters(calculationDateString.value).nhgPremie *
					leninghoogteToPayNHG
				: Number.NaN;

			if (
				Number.isFinite(nhgBedrag) &&
				data.value.financieringsOpzet.aankoopkosten
			) {
				data.value.financieringsOpzet.aankoopkosten.nhgHoogte = nhgBedrag;
			} else {
				delete data.value.financieringsOpzet.aankoopkosten?.nhgHoogte;
			}
		} else {
			delete data.value.financieringsOpzet.aankoopkosten?.nhgHoogte;
		}
	});

	// Clean up NHG fields when NHG is disabled
	watch(
		() => data.value.financieringsOpzet?.useNHG,
		(useNHG) => {
			if (useNHG !== "Ja" && data.value.financieringsOpzet?.aankoopkosten) {
				delete data.value.financieringsOpzet.nhgInputType;
				delete data.value.financieringsOpzet.aankoopkosten.nhgHoogte;
				delete data.value.financieringsOpzet.aankoopkosten.nhgHoogteManual;
			}
		},
	);

	// Bereken totaal nodig
	watchEffect(() => {
		if (!data.value.financieringsOpzet) {
			return;
		}

		// Create a copy of aankoopkosten to calculate total, ensuring only one NHG value is included
		const aankoopkostenForTotal = {
			...data.value.financieringsOpzet.aankoopkosten,
		};

		// Ensure only one NHG value is included in the total
		const nhgInputType = data.value.financieringsOpzet?.nhgInputType;
		const useNHG = data.value.financieringsOpzet?.useNHG;

		if (nhgInputType === "manual") {
			// Use manual value, remove computed value
			delete aankoopkostenForTotal?.nhgHoogte;
		} else if (nhgInputType === "auto" || (useNHG === "Ja" && !nhgInputType)) {
			// Use computed value, remove manual value (default to auto if nhgInputType is missing but NHG is enabled)
			delete aankoopkostenForTotal?.nhgHoogteManual;
		} else {
			// No NHG selected, remove both values
			delete aankoopkostenForTotal?.nhgHoogte;
			delete aankoopkostenForTotal?.nhgHoogteManual;
		}

		// Ensure only one OVB value is included in the total
		const percentageOvb = data.value.financieringsOpzet?.percentageOvb;

		if (percentageOvb === "manual") {
			// Use manual value, remove computed value
			delete aankoopkostenForTotal?.ovb;
		} else {
			// Use computed value, remove manual value
			delete aankoopkostenForTotal?.ovbManual;
		}
		const totaalNodig = sumInObject(aankoopkostenForTotal);

		if (data.value.financieringsOpzet.resultaat === undefined) {
			data.value.financieringsOpzet.resultaat = {};
		}

		if (Number.isNaN(totaalNodig)) {
			delete data.value.financieringsOpzet.totaalNodig;
			delete data.value.financieringsOpzet.resultaat.kosten;
		} else {
			data.value.financieringsOpzet.totaalNodig = totaalNodig;
			data.value.financieringsOpzet.resultaat.kosten = totaalNodig;
		}
	});

	// Bereken eigen middelen als restpost (alleen auto-mode)
	watchEffect(() => {
		if (!data.value.financieringsOpzet) return;
		if (data.value.financieringsOpzet.eigenMiddelenInputType !== "auto") return;

		const totaalNodig = data.value.financieringsOpzet.totaalNodig;
		const leningdeelTotal = asCur(mortgagePartsTotal.value?.hoogte);

		const bf = data.value.financieringsOpzet.benodigdeFinanciering;
		const overigeMiddelen =
			(asCur(bf?.starterslening) ?? 0) +
			(asCur(bf?.overwaarde) ?? 0) +
			(asCur(bf?.lening) ?? 0) +
			(asCur(bf?.overbruggingskrediet) ?? 0);

		if (!Number.isFinite(totaalNodig)) {
			delete data.value.financieringsOpzet.benodigdeFinanciering?.eigenMiddelen;
			return;
		}

		const eigenMiddelen =
			(totaalNodig ?? 0) - (leningdeelTotal ?? 0) - overigeMiddelen;

		if (!data.value.financieringsOpzet.benodigdeFinanciering) {
			data.value.financieringsOpzet.benodigdeFinanciering = {};
		}

		data.value.financieringsOpzet.benodigdeFinanciering.eigenMiddelen =
			Math.max(0, eigenMiddelen);
	});

	watchEffect(() => {
		if (!data.value.financieringsOpzet) {
			return;
		}

		const reedsGefinancierd = sumInObject(
			data.value.financieringsOpzet.benodigdeFinanciering,
		);

		if (data.value.financieringsOpzet.resultaat === undefined) {
			data.value.financieringsOpzet.resultaat = {};
		}

		if (!Number.isFinite(reedsGefinancierd)) {
			delete data.value.financieringsOpzet.middelenResultaat;
			delete data.value.financieringsOpzet.resultaat.middelen;
		} else {
			data.value.financieringsOpzet.middelenResultaat = reedsGefinancierd;
			data.value.financieringsOpzet.resultaat.middelen =
				reedsGefinancierd === 0 ? 0 : reedsGefinancierd * -1;
		}
	});

	watchEffect(() => {
		if (!data.value.financieringsOpzet) {
			return;
		}

		const totaalNodig = data.value.financieringsOpzet.totaalNodig;
		const middelenResultaat = data.value.financieringsOpzet.middelenResultaat;

		const benodigdeFinanciering =
			totaalNodig !== undefined && middelenResultaat !== undefined
				? totaalNodig - middelenResultaat
				: NaN;

		if (!Number.isFinite(benodigdeFinanciering)) {
			delete data.value.financieringsOpzet.benodigdeFinancieringResultaat;
		} else {
			data.value.financieringsOpzet.benodigdeFinancieringResultaat =
				benodigdeFinanciering;
		}
	});

	watch(
		() => data.value.financieringsOpzet?.gebruiktBankgarantie,
		(gebruiktBankgarantie) => {
			if (!data.value.financieringsOpzet?.aankoopkosten) {
				return;
			}

			if (
				gebruiktBankgarantie === "Ja (handmatig)" &&
				!data.value.financieringsOpzet.aankoopkosten.bankgarantieManual
			) {
				data.value.financieringsOpzet.aankoopkosten.bankgarantieManual =
					data.value.financieringsOpzet.aankoopkosten.bankgarantie;
			}

			if (gebruiktBankgarantie !== "Ja (handmatig)") {
				data.value.financieringsOpzet.aankoopkosten.bankgarantieManual =
					undefined;
			}

			if (gebruiktBankgarantie !== "Ja (berekend)") {
				data.value.financieringsOpzet.aankoopkosten.bankgarantie = undefined;
			}
		},
		{ immediate: true },
	);

	// Bereken bankgarantie
	watch(
		[
			() => data.value.financieringsOpzet?.aankoopkosten?.koopsom,
			() => data.value.financieringsOpzet?.gebruiktBankgarantie,
			() => data.value.financieringsOpzet?.aankoopkosten?.bankgarantie,
			calculationDateString,
		],
		() => {
			if (
				data.value.financieringsOpzet.aankoopkosten?.koopsom !== undefined &&
				data.value.financieringsOpzet.gebruiktBankgarantie === "Ja (berekend)"
			) {
				const koopsom = asCur(
					data.value.financieringsOpzet.aankoopkosten?.koopsom,
				);

				// De hoogte van de bankgarantie exclusief bijkomende kosten
				const garantieBedrag =
					koopsom !== undefined
						? useParameters(calculationDateString.value)
								.bankgarantiePercentageKoopsom * koopsom
						: NaN;

				// Garantiekosten is 0,9% van het garantiebedrag met een minimum van 225 euro.
				let garantieKosten =
					useParameters(calculationDateString.value).bankgarantieBankkosten *
					garantieBedrag;
				garantieKosten = Math.max(
					useParameters(calculationDateString.value)
						.bankgarantiekostenMinimaleHoogte,
					garantieKosten,
				);

				// Administratievergoeding is bij HelloHypotheek altijd 0.2%
				const administratieVergoeding =
					useParameters(calculationDateString.value)
						.bankgarantieAdministratiekostenPercentage * garantieBedrag;

				data.value.financieringsOpzet.aankoopkosten.bankgarantie =
					garantieKosten + administratieVergoeding;
			}
		},
	);

	// Bereken financieringsresultaat
	watchEffect(() => {
		if (!data.value.financieringsOpzet) {
			return;
		}

		let benodigdeFinanciering = asCur(
			data.value.financieringsOpzet.benodigdeFinancieringResultaat,
		);
		let leningdeelTotal = asCur(mortgagePartsTotal.value?.hoogte);

		let result: number | undefined;
		if (
			!Number.isFinite(benodigdeFinanciering) &&
			!Number.isFinite(leningdeelTotal)
		) {
			result = undefined;
		} else {
			if (!Number.isFinite(benodigdeFinanciering)) {
				benodigdeFinanciering = 0.0;
			}

			if (!Number.isFinite(leningdeelTotal)) {
				leningdeelTotal = 0.0;
			}

			result = (benodigdeFinanciering ?? 0) - (leningdeelTotal ?? 0);
		}

		if (data.value.financieringsOpzet.financiering === undefined) {
			data.value.financieringsOpzet.financiering = {};
		}

		data.value.financieringsOpzet.financiering.financieringsresultaat = result;

		if (!Number.isFinite(result)) {
			delete data.value.financieringsOpzet.financiering.financieringsresultaat;
		}

		let label: string;
		let className: string;
		if (result && result > 0) {
			label = "Tekort aan middelen";
			className = "result failure";
		} else if (result && result < 0) {
			label = "Te veel financiering";
			className = "result success";
		} else {
			label = "Financieringsresultaat";
			className = "result neutral";
		}

		data.value.financieringsOpzet.resultInfo = {
			label: label,
			className: className,
		};
	});

	// Bereken en toon financieringsresultaat
	watchEffect(() => {
		if (!data.value.financieringsOpzet) {
			return;
		}

		let index = 0;

		const leningdelenSchema: Record<string, LeningdeelVariable> = {};

		if (data.value.financieringsOpzet.financiering === undefined) {
			data.value.financieringsOpzet.financiering = {};
		}

		if (data.value.financieringsOpzet.leningdelen === undefined) {
			data.value.financieringsOpzet.leningdelen = {};
		}

		if (data.value.financieringsOpzet.newLoanpartsTotal === undefined) {
			data.value.financieringsOpzet.newLoanpartsTotal = {};
		}

		if (data.value.financieringsOpzet.resultaat === undefined) {
			data.value.financieringsOpzet.resultaat = {};
		}

		const newLeningdelenTotal = shownMortgagePartsTotal.value;
		data.value.financieringsOpzet.newLoanpartsTotal.hoogte =
			newLeningdelenTotal;

		if (typeof newLeningdelenTotal === "number") {
			data.value.financieringsOpzet.resultaat.leningdelen =
				newLeningdelenTotal === 0 ? 0 : newLeningdelenTotal * -1;
		} else {
			data.value.financieringsOpzet.resultaat.leningdelen = undefined;
		}

		for (let i = 0; i < mortgagePartsToShow.value.length; i++) {
			const deel = data.value.mortgage.mortgageParts[i];
			if (!deel) {
				continue;
			}

			const deelSummary: LeningdeelVariable = {
				component: RowText,
				label: deel.repaymentForm
					? repaymentFormLabels[deel.repaymentForm]
					: `Leningdeel ${i + 1}`,
				readOnly: true,
				labelForPDF: getLabelForPDF(deel, i),
				valueType: ValueType.Currency as const,
			};

			const leningdeelIndex = `deel ${index}`;

			leningdelenSchema[leningdeelIndex] = deelSummary;
			data.value.financieringsOpzet.leningdelen[leningdeelIndex] = asCur(
				deel.amount,
			);

			index++;
		}

		state.leningdelenSchema.leningdelen.schema = leningdelenSchema;
	});

	return {
		state,
		shownMortgagePartsTotal,
		calculationDate,
		calculationDateString,
	};
});
